import { ScrollToTop } from "@/components/common/ScrollToTop";
import { Interview } from "@/components/examinee/interview/Interview";
import { postPrimaryInterviewAnswer } from "@/components/examinee/interview/InterviewAPI";
import { PrimaryInterviewPageSummary } from "@/components/examinee/interview/PageSummary";
import { paths } from "@/components/examinee/interview/Path";
import {
  InterviewAnswer,
  InterviewEntry,
} from "@/components/examinee/interview/Types";
import { useSleepCheckupInfoAPI } from "@/components/examinee/sleep_checkup/SleepCheckupAPI";
import { ExamineePageProps } from "@/pages/examinee/ExamineePageProps";
import { useEffect, useRef } from "react";
import { Params, useParams } from "react-router-dom";

export function PrimaryInterview({ headerHeight }: ExamineePageProps) {
  const api = useSleepCheckupInfoAPI();
  const params = useParams();
  // 新フロー判定。取得失敗・未取得時は false のまま旧動線（通知メール設定画面）に
  // フォールバックする（新フローで旧動線に落ちても POST /api/reminds/ は
  // バックエンドのガードで拒否される）。ref にすることで再レンダリング
  // （Interview の context 再生成による再フェッチ）を避ける
  const isNewFlowRef = useRef(false);

  useEffect(() => {
    (async () => {
      if (params.sleepCheckupId == null) return;
      try {
        const info = await api.get(params.sleepCheckupId);
        isNewFlowRef.current = info.data.is_new_flow;
      } catch {
        // フォールバック（旧動線）のため何もしない
      }
    })();
  }, [api, params.sleepCheckupId]);

  const getNextPage = (params: Readonly<Params<string>>) => {
    if (params.sleepCheckupId == null || params.sleepCheckupId === undefined) {
      throw new Error("sleepCheckupId is not assigned");
    }

    // 新フローは通知メール設定を測定期間登録に集約するため、
    // 通知メール設定画面をスキップして完了画面へ直行する（#1762）
    if (isNewFlowRef.current) {
      return paths.getFullPath("PrimaryInterviewCompletion");
    }

    return paths.getFullPath("DailyInterviewRemind", {
      ":sleepCheckupId": params.sleepCheckupId,
    });
  };

  return (
    <>
      <ScrollToTop />
      <Interview
        pageProps={{ headerHeight: headerHeight }}
        context={{
          pageSummaryCreator: (questionScreen) =>
            new PrimaryInterviewPageSummary(questionScreen),
          postAnswer: postAnswer,
          getNextScreen: getNextScreen,
          getNextPage: getNextPage,
        }}
      />
    </>
  );
}

function postAnswer(
  params: Readonly<Params<string>>,
  interviewAnswer: InterviewAnswer,
  currentPath: string
) {
  if (params.sleepCheckupId == null || params.sleepCheckupId === undefined) {
    throw new Error("sleepCheckupId is not assigned");
  }

  return postPrimaryInterviewAnswer(
    params.sleepCheckupId,
    interviewAnswer,
    currentPath
  );
}

function getNextScreen(
  entry: InterviewEntry,
  params: Readonly<Params<string>>
) {
  if (params.sleepCheckupId == null || params.sleepCheckupId === undefined) {
    throw new Error("sleepCheckupId is not assigned");
  }
  if (params.interviewId == null || params.interviewId === undefined) {
    throw new Error("interviewId is not assigned");
  }

  return paths.getFullPath("PrimaryInterview", {
    ":sleepCheckupId": params.sleepCheckupId,
    ":interviewId": params.interviewId,
    ":screen": String(entry.screen.id),
  });
}
